/* N'kissi interactions: reveal, gallery, quantity, AJAX add-to-bag
   (opens Horizon's cart drawer), sticky ATC, quiz, newsletter. */
import { CartAddEvent } from '@theme/events';

const qs = (s, r = document) => r.querySelector(s);
const qsa = (s, r = document) => Array.from(r.querySelectorAll(s));

/* ---------- Reveal on scroll ---------- */
function initReveal(root = document) {
  const els = qsa('.nk-reveal:not(.is-in)', root);
  if (!('IntersectionObserver' in window)) {
    els.forEach((el) => el.classList.add('is-in'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  els.forEach((el) => io.observe(el));
}

/* ---------- Add to bag ---------- */
async function addToCart(variantId, quantity, sourceEl, sellingPlan) {
  const sectionIds = qsa('cart-items-component')
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  const item = { id: Number(variantId), quantity: Number(quantity) || 1 };
  if (sellingPlan) item.selling_plan = Number(sellingPlan);
  const body = { items: [item] };
  if (sectionIds.length) body.sections = sectionIds.join(',');

  const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to bag');

  const cart = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js').then((r) => r.json());
  const drawer = qs('cart-drawer-component');
  (sourceEl || document).dispatchEvent(
    new CartAddEvent(cart, String(variantId), {
      source: 'nkissi',
      itemCount: Number(quantity) || 1,
      variantId: String(variantId),
      sections: data.sections,
    })
  );
  if (!drawer) window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
  else if (!drawer.hasAttribute('auto-open') && typeof drawer.showDialog === 'function') drawer.showDialog();
  return cart;
}

function bindAddButtons(root = document) {
  qsa('[data-nk-add]', root).forEach((btn) => {
    if (btn.dataset.nkBound) return;
    btn.dataset.nkBound = '1';
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const form = btn.closest('form');
      const qtyInput = form ? qs('input[name="quantity"]', form) : null;
      const variant = btn.dataset.variant || (form && qs('[name="id"]', form)?.value);
      const err = form ? qs('.nk-buy__error', form.parentElement) : null;
      const label = btn.innerHTML;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      try {
        const plan = form ? qs('[name="selling_plan"]', form)?.value : '';
        await addToCart(variant, qtyInput ? qtyInput.value : 1, btn, plan);
        btn.innerHTML = btn.dataset.added || 'Added to bag';
        setTimeout(() => (btn.innerHTML = label), 1800);
        if (err) err.textContent = '';
      } catch (ex) {
        if (err) err.textContent = ex.message;
        else btn.innerHTML = ex.message;
        setTimeout(() => (btn.innerHTML = label), 2600);
      } finally {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      }
    });
  });
}

/* ---------- Quantity ---------- */
function bindQty(root = document) {
  qsa('.nk-qty', root).forEach((q) => {
    if (q.dataset.nkBound) return;
    q.dataset.nkBound = '1';
    const input = qs('input', q);
    qsa('button', q).forEach((b) =>
      b.addEventListener('click', () => {
        const v = Math.max(1, (parseInt(input.value, 10) || 1) + Number(b.dataset.step));
        input.value = v;
      })
    );
  });
}

/* ---------- Gallery ---------- */
function bindGallery(root = document) {
  qsa('[data-nk-gallery]', root).forEach((g) => {
    if (g.dataset.nkBound) return;
    g.dataset.nkBound = '1';
    const track = qs('.nk-gallery__main', g);
    const thumbs = qsa('.nk-gallery__thumb', g);
    const dots = qsa('.nk-gallery__dots span', g);
    const go = (i) => track.scrollTo({ left: track.clientWidth * i, behavior: 'smooth' });
    thumbs.forEach((t, i) => t.addEventListener('click', () => go(i)));
    let raf;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(track.scrollLeft / track.clientWidth);
        thumbs.forEach((t, j) => t.setAttribute('aria-current', j === i ? 'true' : 'false'));
        dots.forEach((d, j) => d.classList.toggle('is-on', j === i));
      });
    });
  });
}

/* ---------- Sticky add to cart ---------- */
function bindSticky(root = document) {
  const bar = qs('.nk-sticky-atc', root);
  const anchor = qs('[data-nk-buy-anchor]', root);
  if (!bar || !anchor || !('IntersectionObserver' in window)) return;
  const footer = qs('.nk-footer');
  let pastBuy = false;
  let atFooter = false;
  const update = () => bar.classList.toggle('is-visible', pastBuy && !atFooter);
  new IntersectionObserver(([e]) => {
    pastBuy = !e.isIntersecting && e.boundingClientRect.top < 0;
    update();
  }).observe(anchor);
  if (footer)
    new IntersectionObserver(([e]) => {
      atFooter = e.isIntersecting;
      update();
    }).observe(footer);
}

/* ---------- Quiz ---------- */
function bindQuiz(root = document) {
  qsa('[data-nk-quiz]', root).forEach((quiz) => {
    if (quiz.dataset.nkBound) return;
    quiz.dataset.nkBound = '1';
    const steps = qsa('.nk-quiz__step', quiz);
    const bars = qsa('.nk-quiz__progress span', quiz);
    const dataEl = qs('script[type="application/json"]', quiz);
    const products = dataEl ? JSON.parse(dataEl.textContent) : {};
    const answers = {};
    let idx = 0;

    const show = (i) => {
      idx = i;
      steps.forEach((s, j) => s.classList.toggle('is-active', j === i));
      bars.forEach((b, j) => b.classList.toggle('is-on', j <= i));
    };

    const recommend = () => {
      const score = { d3: 1, dermera: 0, rooted: 0 };
      const goal = answers.goal;
      const stage = answers.stage;
      if (goal === 'balance') score.rooted += 3;
      if (goal === 'beauty') score.dermera += 3;
      if (goal === 'foundation') score.d3 += 3;
      if (goal === 'all') { score.rooted += 2; score.dermera += 2; score.d3 += 2; }
      if (stage === 'cycle') score.rooted += 2;
      if (stage === 'peri' || stage === 'meno') { score.rooted += 2; score.d3 += 2; }
      if (stage === 'thrive') { score.dermera += 1; score.d3 += 1; }
      if (answers.focus === 'sun') score.d3 += 2;
      if (answers.focus === 'mood') score.rooted += 2;
      if (answers.focus === 'glow') score.dermera += 2;
      return Object.entries(score)
        .sort((a, b) => b[1] - a[1])
        .filter(([, v], i) => i === 0 || v >= 3)
        .slice(0, 2)
        .map(([k]) => k);
    };

    const renderResult = () => {
      const list = qs('.nk-quiz__recs', quiz);
      const why = qs('.nk-quiz__why', quiz);
      const keys = recommend();
      list.innerHTML = '';
      keys.forEach((k) => {
        const p = products[k];
        if (!p) return;
        const row = document.createElement('div');
        row.className = 'nk-quiz__rec';
        row.style.setProperty('--tint', p.tint || '');
        row.innerHTML = `
          <a href="${p.url}"><img src="${p.image}" alt="" width="84" height="84" loading="eager"></a>
          <div><strong>${p.title}</strong><span>${p.tagline || ''}</span></div>
          ${p.variant ? `<button type="button" class="nk-btn nk-btn--sm" data-nk-add data-variant="${p.variant}">Add · ${p.price}</button>` : `<a class="nk-btn nk-btn--sm" href="${p.url}">View</a>`}`;
        list.appendChild(row);
      });
      const msgs = {
        balance: 'Botanicals traditionally used for hormonal balance and mood, plus the foundation your body relies on.',
        beauty: 'A beauty-from-within ritual for hair, skin and nails, built on daily essentials.',
        foundation: 'Start with the essentials: bone, immune and muscle support you can feel good about.',
        all: 'A complete N’kissi ritual for whole-body support, from the inside out.',
      };
      if (why) why.textContent = msgs[answers.goal] || '';
      bindAddButtons(list);
    };

    qsa('.nk-quiz__opt', quiz).forEach((opt) =>
      opt.addEventListener('click', () => {
        const step = opt.closest('.nk-quiz__step');
        qsa('.nk-quiz__opt', step).forEach((o) => o.setAttribute('aria-pressed', 'false'));
        opt.setAttribute('aria-pressed', 'true');
        answers[step.dataset.key] = opt.dataset.value;
        setTimeout(() => {
          if (idx + 1 === steps.length - 1) renderResult();
          show(idx + 1);
        }, 260);
      })
    );
    qsa('[data-nk-quiz-back]', quiz).forEach((b) => b.addEventListener('click', () => show(Math.max(0, idx - 1))));
    qsa('[data-nk-quiz-restart]', quiz).forEach((b) => b.addEventListener('click', () => show(0)));
    show(0);
  });
}

/* ---------- Subscription picker ---------- */
function bindPlans(root = document) {
  qsa('[data-nk-plans]', root).forEach((fs) => {
    if (fs.dataset.nkBound) return;
    fs.dataset.nkBound = '1';
    const hidden = qs('[name="selling_plan"]', fs);
    const select = qs('.nk-plan__select', fs);
    const sync = () => {
      const sub = qs('[name="nk_purchase"]:checked', fs)?.value === 'sub';
      hidden.value = sub && select ? select.value : '';
      qsa('.nk-plan', fs).forEach((l) => l.classList.toggle('is-on', qs('input', l).checked));
      syncInjectedWidget(fs.closest('form'));
    };
    qsa('[name="nk_purchase"]', fs).forEach((r) => r.addEventListener('change', sync));
    if (select) select.addEventListener('change', () => {
      const r = qs('[value="sub"]', fs);
      if (r) r.checked = true;
      sync();
    });
    sync();
  });
  qsa('[data-nk-proxy]', root).forEach((b) => {
    if (b.dataset.nkBound) return;
    b.dataset.nkBound = '1';
    b.addEventListener('click', () => qs(b.dataset.nkProxy)?.click());
  });
}

/* Rhem (Appstle) injects a hidden widget into product forms and rewrites /cart/add
   requests with that widget's selection. Mirror the N'kissi picker into it so the
   plan it injects always matches what the customer chose. */
function syncInjectedWidget(form) {
  const widget = form && qs('.appstle_sub_widget, [id^="appstle_subscription_widget"]', form);
  if (!widget) return;
  const plan = qs('[name="selling_plan"]', qs('[data-nk-plans]', form) || form)?.value || '';
  const radios = qsa('input[type="radio"]', widget);
  radios.forEach((r) => (r.disabled = false));
  const target = plan
    ? radios.find((r) => r.dataset.appstleRealValue) || radios[1]
    : radios.find((r) => !r.dataset.appstleRealValue) || radios[0];
  if (plan) {
    const sel = qs('select', widget);
    if (sel && [...sel.options].some((o) => o.value === plan)) {
      sel.value = plan;
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  if (target && !target.checked) target.click();
  // Rhem only fills the real plan id into the radio from its own handlers; set it directly.
  radios.forEach((r) => {
    if (r.dataset.appstleRealValue) r.value = r === target && plan ? plan : '';
    r.checked = r === target;
  });
}

function watchInjectedWidget(root = document) {
  qsa('.nk-buy__form', root).forEach((form) => {
    if (form.dataset.nkWatch) return;
    form.dataset.nkWatch = '1';
    new MutationObserver(() => {
      if (qs('.appstle_sub_widget', form) && !form.dataset.nkSynced) {
        form.dataset.nkSynced = '1';
        syncInjectedWidget(form);
      }
    }).observe(form, { childList: true, subtree: true });
    syncInjectedWidget(form);
  });
}

function init(root = document) {
  bindPlans(root);
  watchInjectedWidget(root);
  initReveal(root);
  bindAddButtons(root);
  bindQty(root);
  bindGallery(root);
  bindSticky(root);
  bindQuiz(root);
}

init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
